import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router';

import { Avatar } from '@/components/Avatar';
import { ArrowRightIcon, BridgeMark, CheckIcon, FileIcon, LockIcon } from '@/components/Icons';
import { Page } from '@/components/Layout';
import { PasswordPlate } from '@/components/PasswordPlate';
import { QrImage } from '@/components/QrImage';
import { tr, useT, type MessageKey } from '@/i18n';
import ui from '@/components/ui.module.css';

import { CasesHero } from './CasesHero';
import styles from './CasesPage.module.css';

/*
 * Cases: why one would want Bridge, for someone who has just heard of it. Not screenshots of whole screens but a
 * bento of tiles of different sizes, each a short claim and a small stage built from real pieces of the interface —
 * the password plate, the emoji both devices show, the QR code, the chat — laid over each other with depth.
 * The pieces are illustrations (aria-hidden); each tile's words say what they show.
 */

/** A CSS module class, never undefined. */
const c = (name: string | undefined): string => name ?? '';

/** A piece of the interface on a stage: a card with its own hairline and a soft, layered shadow. */
function Piece({ className, children }: { readonly className?: string; readonly children: ReactNode }): React.JSX.Element {
  return <div className={[styles.piece, className ?? ''].join(' ')}>{children}</div>;
}

/** The password that opens once: a chat with a link in it, the plate the link opens, and the view it spent. */
function PasswordStage(): React.JSX.Element {
  const t = useT();
  return (
    <>
      <Piece className={c(styles.pwChat)}>
        <p className={c(styles.chatHead)}>{t('cases.teamChat')}</p>
        <p className={c(styles.bubbleIn)}>{t('cases.askPassword')}</p>
        <div className={c(styles.bubbleOut)}>
          <span className={c(styles.linkChip)}>
            <BridgeMark size={12} />
            {t('cases.bridgeLink')}
          </span>
          <span className={c(styles.linkPath)}>/b/q7RkXw2#·······</span>
        </div>
      </Piece>
      <Piece className={c(styles.pwPlate)}>
        <PasswordPlate password="vT7#qL9!xR2@mK4s" shown={false} onToggle={() => undefined} />
        <p className={c(styles.pwNote)}>{t('cases.stagingNote')}</p>
      </Piece>
      <span className={[styles.chip, styles.pwChip].join(' ')}>
        <i className={c(styles.dotGreen)} />
        {t('cases.openedOnce')}
      </span>
    </>
  );
}

const SAS = ['🐙', '🚀', '🌷'] as const;

/** Nearby: the laptop offers, the phone asks, both show the same four emoji. */
function NearbyStage(): React.JSX.Element {
  const t = useT();
  return (
    <>
      <Piece className={c(styles.nbLaptop)}>
        <p className={c(styles.small)}>{t('cases.sendingTo')}</p>
        <p className={c(styles.device)}>
          <Avatar seed="Swift Fox" size={28} className={c(styles.face)} />
          Swift Fox
        </p>
        <p className={c(styles.emoji)}>{SAS.join(' ')}</p>
        <p className={c(styles.small)}>{t('cases.sameOnPhone')}</p>
      </Piece>
      <Piece className={c(styles.nbPhone)}>
        <span className={c(styles.island)} />
        <p className={c(styles.device)}>
          <Avatar seed="Calm Otter" size={28} className={c(styles.face)} />
          Calm Otter
        </p>
        <p className={c(styles.small)}>{t('cases.wantsToSend')}</p>
        <p className={c(styles.emoji)}>{SAS.join(' ')}</p>
        <span className={c(styles.accept)}>{t('cases.accept')}</span>
        <span className={c(styles.decline)}>{t('cases.decline')}</span>
      </Piece>
    </>
  );
}

/** Receive: a QR code on the computer that is not yours, and the file arriving on it. */
function ReceiveStage(): React.JSX.Element {
  const t = useT();
  return (
    <>
      <Piece className={c(styles.rcQr)}>
        <QrImage value={`${window.location.origin}/connect`} size={112} alt="" className={c(styles.qr)} />
        <p className={c(styles.small)}>{t('cases.scanToSend')}</p>
      </Piece>
      <Piece className={c(styles.rcFile)}>
        <span className={c(styles.fileIcon)}>
          <FileIcon size={18} />
        </span>
        <span className={c(styles.fileText)}>
          <b>boarding-pass.pdf</b>
          <small>{t('cases.received')}</small>
        </span>
        <CheckIcon size={16} className={c(styles.check)} />
      </Piece>
    </>
  );
}

/** A room for two: a few messages each way and a file. */
function ChatStage(): React.JSX.Element {
  const t = useT();
  return (
    <Piece className={c(styles.chChat)}>
      <p className={c(styles.msgIn)}>
        <Avatar seed="room:guest" size={24} className={c(styles.face)} />
        <span>{t('cases.vpnPlease')}</span>
      </p>
      <p className={c(styles.msgOut)}>
        <span className={c(styles.msgFile)}>
          <FileIcon size={14} />
          office.ovpn
        </span>
        <Avatar seed="room:owner" size={24} className={c(styles.face)} />
      </p>
      <p className={c(styles.msgIn)}>
        <Avatar seed="room:guest" size={24} className={c(styles.face)} />
        <span>{t('cases.gotIt')}</span>
      </p>
    </Piece>
  );
}

/** The choices of the lifetime shown on the stage; the second one is on. */
const TTL_CHOICES: readonly { readonly key: string; readonly label: MessageKey; readonly params: { readonly n: number } }[] = [
  { key: 'hour1', label: 'cases.hours', params: { n: 1 } },
  { key: 'day1', label: 'cases.days', params: { n: 1 } },
  { key: 'day3', label: 'cases.days', params: { n: 3 } },
  { key: 'day7', label: 'cases.days', params: { n: 7 } },
];

/** How long it lives: the expiry and the views, and a countdown that really runs. */
function TimeStage(): React.JSX.Element {
  const t = useT();
  const [left, setLeft] = useState(24 * 3600 - 19);
  useEffect(() => {
    const timer = window.setInterval(() => {
      setLeft((value) => (value > 1 ? value - 1 : 24 * 3600));
    }, 1000);
    return () => {
      window.clearInterval(timer);
    };
  }, []);
  const clock = [Math.floor(left / 3600), Math.floor((left % 3600) / 60), left % 60].map((part) => String(part).padStart(2, '0')).join(':');
  return (
    <>
      <Piece className={c(styles.tmControls)}>
        <p className={c(styles.small)}>{t('cases.expiresIn')}</p>
        <span className={c(styles.seg)}>
          {TTL_CHOICES.map(({ key, label, params }) => (
            <i key={key} className={key === 'day1' ? styles.segOn : ''}>
              {t(label, params)}
            </i>
          ))}
        </span>
        <p className={c(styles.small)}>{t('common.views')}</p>
        <span className={c(styles.seg)}>
          {['1', '5', '10', '25'].map((label) => (
            <i key={label} className={label === '1' ? styles.segOn : ''}>
              {label}
            </i>
          ))}
        </span>
      </Piece>
      <span className={[styles.chip, styles.tmChip].join(' ')}>
        {tr('cases.erasedIn', { clock: (children) => <b className={c(styles.clock)}>{children}</b> }, { time: clock })}
      </span>
    </>
  );
}

/** The key never reaches the server: the link, its fragment, and what the server keeps. */
function KeyStage(): React.JSX.Element {
  const t = useT();
  return (
    <>
      <Piece className={c(styles.kyUrl)}>
        <LockIcon size={14} className={c(styles.lock)} />
        <span className={c(styles.urlPath)}>bridge/b/q7RkXw2</span>
        <span className={c(styles.urlKey)}>
          #Vx9pL2fQa8Tz
          <span className={c(styles.callout)}>{t('cases.keyCallout')}</span>
        </span>
      </Piece>
      <Piece className={c(styles.kyServer)}>
        <p className={c(styles.small)}>{t('cases.serverKeeps')}</p>
        <code className={c(styles.cipher)}>
          a1 9f 3c 7e 02 d4 b8 61 5a ee
          <br />
          47 f1 2b 8d c6 10 7a 39 e5 54
          <br />
          9e 66 13 ac 70 d2 4f 81 2c b7
        </code>
      </Piece>
    </>
  );
}

/** From a script: one request, one link. */
function DevStage(): React.JSX.Element {
  return (
    <Piece className={c(styles.dvTerm)}>
      <span className={c(styles.termBar)}>
        <i />
        <i />
        <i />
      </span>
      <code className={c(styles.term)}>
        <span className={c(styles.prompt)}>$</span> curl -X POST $BRIDGE/api/bridges
        <br />
        <span className={c(styles.out)}>{'{ "bridgeId": "K7mN9pQr…", … }'}</span>
        <br />
        <span className={c(styles.link)}>$BRIDGE/b/K7mN9pQr#key</span>
      </code>
    </Piece>
  );
}

interface Tile {
  readonly id: string;
  readonly name: MessageKey;
  readonly title: MessageKey;
  readonly text: MessageKey;
  readonly action: { readonly label: MessageKey; readonly to: string };
  readonly size: 'large' | 'side' | 'third' | 'wide' | 'rest';
  readonly Stage: () => React.JSX.Element;
}

const TILES: readonly Tile[] = [
  {
    id: 'passwords',
    name: 'cases.namePasswords',
    title: 'cases.passwordsTitle',
    text: 'cases.passwordsText',
    action: { label: 'cases.passwordsAction', to: '/?do=password' },
    size: 'large',
    Stage: PasswordStage,
  },
  {
    id: 'nearby',
    name: 'cases.nameNearby',
    title: 'cases.nearbyTitle',
    text: 'cases.nearbyText',
    action: { label: 'cases.nearbyAction', to: '/' },
    size: 'side',
    Stage: NearbyStage,
  },
  {
    id: 'receive',
    name: 'cases.nameReceive',
    title: 'cases.receiveTitle',
    text: 'cases.receiveText',
    action: { label: 'cases.receiveAction', to: '/?do=receive' },
    size: 'third',
    Stage: ReceiveStage,
  },
  {
    id: 'chat',
    name: 'cases.nameChat',
    title: 'cases.chatTitle',
    text: 'cases.chatText',
    action: { label: 'cases.chatAction', to: '/?do=chat' },
    size: 'third',
    Stage: ChatStage,
  },
  {
    id: 'time',
    name: 'cases.nameTime',
    title: 'cases.timeTitle',
    text: 'cases.timeText',
    action: { label: 'cases.timeAction', to: '/?do=password' },
    size: 'third',
    Stage: TimeStage,
  },
  {
    id: 'key',
    name: 'cases.nameKey',
    title: 'cases.keyTitle',
    text: 'cases.keyText',
    action: { label: 'cases.keyAction', to: '/about' },
    size: 'wide',
    Stage: KeyStage,
  },
  {
    id: 'developers',
    name: 'nav.developers',
    title: 'cases.devTitle',
    text: 'cases.devText',
    action: { label: 'nav.developers', to: '/developers' },
    size: 'rest',
    Stage: DevStage,
  },
];

const SIZE: Record<Tile['size'], string | undefined> = {
  large: styles.large,
  side: styles.side,
  third: styles.third,
  wide: styles.wide,
  rest: styles.rest,
};

export function CasesPage(): React.JSX.Element {
  const t = useT();
  return (
    <Page>
      <section className={c(styles.hero)}>
        <div className={c(styles.heroCopy)}>
          <p className={c(styles.eyebrow)}>{t('nav.cases')}</p>
          <h1 className={c(styles.title)}>{t('cases.title')}</h1>
          <p className={c(styles.lead)}>{t('cases.lead')}</p>
          <div className={c(styles.actions)}>
            <Link to="/" className={ui.btn}>
              {t('common.tryBridge')}
              <ArrowRightIcon size={16} />
            </Link>
          </div>
        </div>
        <CasesHero />
      </section>

      <div className={c(styles.bento)}>
        {TILES.map((tile) => (
          <section key={tile.id} id={tile.id} className={[styles.tile, SIZE[tile.size] ?? ''].join(' ')} aria-labelledby={`${tile.id}-title`} data-testid="case">
            <div className={c(styles.copy)}>
              <p className={c(styles.name)}>{t(tile.name)}</p>
              <h2 id={`${tile.id}-title`} className={c(styles.tileTitle)}>
                {t(tile.title)}
              </h2>
              <p className={c(styles.text)}>{t(tile.text)}</p>
              <Link to={tile.action.to} className={c(styles.more)}>
                {t(tile.action.label)}
                <ArrowRightIcon size={14} />
              </Link>
            </div>
            <div className={c(styles.stage)} aria-hidden="true" inert>
              <tile.Stage />
            </div>
          </section>
        ))}
      </div>

      <section className={c(styles.outro)}>
        <h2 className={c(styles.outroTitle)}>{t('cases.outroTitle')}</h2>
        <p className={c(styles.outroText)}>{t('cases.outroText')}</p>
        <div className={c(styles.outroActions)}>
          <Link to="/" className={ui.btn}>
            {t('common.tryBridge')}
            <ArrowRightIcon size={16} />
          </Link>
        </div>
      </section>
    </Page>
  );
}
